"use client"

import { useState, useEffect } from "react"
import { retrieveCustomer } from "../../../../lib/data/customer"
import { HttpTypes } from "@medusajs/types"
import { Button, Input, Label, Textarea, toast, Toaster } from "@medusajs/ui"
import { Star, StarSolid } from "@medusajs/icons"
import { addProductReview } from "../../../../lib/data/products"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"

type ProductReviewsFormProps = {
  productId: string
}

export default function ProductReviewsForm({ productId }: ProductReviewsFormProps) {
  const [customer, setCustomer] = useState<HttpTypes.StoreCustomer | null>(null)
  const [customerLoaded, setCustomerLoaded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [title, setTitle] = useState("")
  const [content, setContent] = useState("")
  const [rating, setRating] = useState(0)

  useEffect(() => {
    retrieveCustomer().then((c) => {
      setCustomer(c)
      setCustomerLoaded(true)
    })
  }, [])

  if (!customerLoaded) {
    return null
  }

  if (!customer) {
    return (
      <div className="product-page-constraint mt-8">
        <div className="flex justify-center">
          <p className="text-base-regular text-ui-fg-subtle">
            <LocalizedClientLink href="/account" className="text-ui-fg-interactive hover:underline">
              Prijavite se za ocenjevanje
            </LocalizedClientLink>
          </p>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (!content || !rating) {
      toast.error("Napaka", {
        description: "Prosimo, izpolnite obvezna polja.",
      })
      return
    }

    e.preventDefault()
    setIsLoading(true)
    addProductReview({
      title,
      content,
      rating,
      first_name: customer.first_name || "",
      last_name: customer.last_name || "",
      product_id: productId,
    }).then(() => {
      setShowForm(false)
      setTitle("")
      setContent("")
      setRating(0)
      toast.success("Uspeh", {
        description: "Vaša ocena je bila poslana in čaka na odobritev.",
      })
    }).catch(() => {
      toast.error("Napaka", {
        description: "Prišlo je do napake pri pošiljanju vaše ocene. Prosimo, poskusite ponovno kasneje.",
      })
    }).finally(() => {
      setIsLoading(false)
    })
  }

  return (
    <div className="product-page-constraint mt-8">
      {!showForm && (
        <div className="flex justify-center">
          <Button variant="secondary" onClick={() => setShowForm(true)}>Dodaj oceno</Button>
        </div>
      )}
      {showForm && (
        <div className="flex flex-col gap-y-4">
          <div className="flex flex-col gap-y-2">
            <span className="text-xl-regular text-ui-fg-base">
            Dodaj oceno
          </span>
          
          <form onSubmit={handleSubmit} className="flex flex-col gap-y-4">
            <div className="flex flex-col gap-y-2">
              <Label>Naslov</Label>
              <Input name="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Naslov" />
            </div>
            <div className="flex flex-col gap-y-2">
              <Label>Vsebina</Label>
              <Textarea name="content" value={content} onChange={(e) => setContent(e.target.value)} placeholder="Vsebina" />
            </div>
            <div className="flex flex-col gap-y-2">
              <Label>Ocena</Label>
              <div className="flex gap-x-1">
                {Array.from({ length: 5 }).map((_, index) => (
                  <Button key={index} variant="transparent" onClick={(e) => {
                    e.preventDefault()
                    setRating(index + 1)
                  }} className="p-0">
                    {rating >= index + 1 ? <StarSolid className="text-ui-tag-orange-icon" /> : <Star />}
                  </Button>
                ))}
              </div>
            </div>
            <Button type="submit" disabled={isLoading} variant="primary">Pošlji</Button>
          </form>
          </div>
        </div>
      )}
      <Toaster />
    </div>
  )
} 